import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import findIndex from 'lodash/findIndex'
import isEmpty from 'lodash/isEmpty'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'

import usePageLoaded from '../../hooks/usePageLoaded'
import { useShowBackButton } from '../../hooks/useBackButton'

import OriginalInfo from '../../components/contribution/originalInfo'
import { ContributionMaterialType, ContributionType } from '../../store/contributions/contributionsTypings'
import { fetchContribution } from '../../store/contributions/contributionsSlice'
import { RootState } from '../../store/rootReducer'
import SwiperCore, { Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import ReactPlayerContributions from './materials/ReactPlayerContributions'

import ImageContribution from './materials/ImageContribution'
import Button from '../../components/ui/button'
import LabelMenu from './materials/labelMenu'
import {
    deleteSwiperTopPosition,
    disableMenu,
    enableMenu,
    pageLoaded,
    scrollBottom,
    scrollToTop,
    setSwiperTopPosition,
    switchMenu
} from '../../store/common/commonSlice'
import ContributionHeader from '../../components/contribution/contributionHeader'

import '../contribution/contribution.sass'
import './contributionMaterial.sass'
import 'swiper/swiper.scss'
import './navigation.scss'
import paths from '../../paths'
import { createLocalizedPath } from '../../utils/localizationHelpers'
import { isUserPro } from '../../utils/isUserPro'
import { META } from '../../constants/meta'
import { getLangFromPath } from '../../i18next'
import { addLocationToString } from '../../utils/meta'

SwiperCore.use([Navigation])

const ContributionMaterial = () => {
    usePageLoaded()
    useShowBackButton()

    const [t, i18n] = useTranslation()
    const location = useLocation()
    const history = useHistory()
    const dispatch = useDispatch()
    const params = useParams<{ material_id: string }>()

    const { loggedIn, user } = useSelector((state: RootState) => state.member)
    const contribution = useSelector<RootState, ContributionType>((state) => state.contributions.contribution)
    const swiperList = useSelector<RootState, ContributionType[]>((state) => state.contributions.swiperList)

    const [slideChanging, setSlideChanging] = useState(false)
    const [newLabels, setNewLabels] = useState([])
    const { materials } = contribution
    const [isLabelMenuOpen, setIsLabelMenuOpen] = useState(false)
    const isFirstLoadingRef = useRef(true)

    const material = contribution.materials.find(
        (material: ContributionMaterialType) => material.id === Number(params.material_id)
    )

    const onSlideChange = (direction: string) => {
        if (slideChanging) {
            const currentIndex = findIndex(materials, material)
            const nextLink = currentIndex + 1 < materials.length ? materials[currentIndex + 1].page_url : null
            const prevLink = currentIndex !== 0 ? materials[currentIndex - 1].page_url : null
            if (nextLink && direction === 'left') {
                history.replace(nextLink)
            }
            if (prevLink && direction === 'right') {
                history.replace(prevLink)
            }
            setSlideChanging(false)
        }
        dispatch(pageLoaded())
    }
    const isVideo = material && material.mime_type.includes('video')

    const keyboardListeners = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            if (swiperList.length) {
                history.go(-1)
            } else {
                history.push(contribution.url)
            }
        }
        //@ts-ignore
        if (event.key === 'ArrowRight') if (!isLabelMenuOpen) document.querySelector('.swiper-button-next').click()
        //@ts-ignore
        if (event.key === 'ArrowLeft') if (!isLabelMenuOpen) document.querySelector('.swiper-button-prev').click()
    }

    useEffect(() => {
        if (isFirstLoadingRef.current) {
            isFirstLoadingRef.current = false
        } else {
            dispatch(fetchContribution({ material_id: String(material.id) }))
        }
    }, [i18n.language])

    useEffect(() => {
        if (
            location.pathname !== material.page_url &&
            typeof window === 'object' &&
            material.page_url.includes(i18n.language)
        ) {
            window.history.replaceState(null, contribution.title, material.page_url)
        }
        // history.replace(material.page_url)
    }, [material])

    useEffect(() => {
        const fullscreenContent = document.getElementById('fullscreenContent')
        const buttonContainer = document.getElementsByClassName('app_container')[0]
        buttonContainer.classList.add('firstChildAbsolute')
        const menuHeight = window.innerWidth < 1700 ? 92 : 20
        //1700 is @media width for change menu position
        //92 is height of menuContainer
        // is margin top of material container
        dispatch(setSwiperTopPosition(+fullscreenContent.offsetTop + menuHeight))
        return () => {
            dispatch(deleteSwiperTopPosition())
            buttonContainer.classList.remove('firstChildAbsolute')
        }
    }, [])

    useEffect(() => {
        document.addEventListener('keydown', keyboardListeners)
        return () => document.removeEventListener('keydown', keyboardListeners)
    }, [isLabelMenuOpen])

    useEffect(() => {
        dispatch(disableMenu())
        return () => {
            dispatch(enableMenu())
        }
    }, [])

    return (
        <div className="materialContainer">
            <Helmet>
                <title>{contribution.title}</title>
                <meta
                    name="description"
                    content={
                        material && material.description
                            ? material.description
                            : addLocationToString(
                                  META.defaultUserMaterial[getLangFromPath(location.pathname)].description.replaceAll(
                                      '{NICK}',
                                      contribution.user.nick || 'Anonymous'
                                  ),
                                  contribution.user
                              ) + ` ${material.tags.map((tag) => tag.text).join(', ')}`
                    }
                />
                <link
                    rel="canonical"
                    href={process.env.CLIENT_PUBLIC_PATH.slice(0, -1) + location.pathname + location.search}
                />
            </Helmet>
            <ContributionHeader contribution={contribution} />
            <div className="headerWrapper  headerWrapper_description">
                <div
                    className="contributionDescription"
                    dangerouslySetInnerHTML={{ __html: contribution.description }}
                />
                <OriginalInfo data={contribution} />
            </div>
            <div className="materialContent fullscreen" id="fullscreenContent">
                <Swiper
                    navigation
                    initialSlide={findIndex(materials, material)}
                    longSwipes={false}
                    className="mySwiper"
                    onSlideChange={() => {
                        setSlideChanging(true)
                    }}
                    onSlideNextTransitionStart={() => {
                        onSlideChange('left')
                    }}
                    onSlidePrevTransitionStart={() => {
                        onSlideChange('right')
                    }}
                >
                    <div className="arrowScrollingContainer upper" onClick={() => dispatch(scrollToTop())}>
                        <img className={'topRotate'} src={require('./../../assets/ic-right-slider-blue.svg')} />
                    </div>
                    {materials.map((item) => (
                        <SwiperSlide key={item.id}>
                            {isVideo ? <ReactPlayerContributions item={item} /> : <ImageContribution item={item} />}
                        </SwiperSlide>
                    ))}
                    <div className="arrowScrollingContainer" onClick={() => dispatch(scrollBottom())}>
                        <img className={'bottomRotate'} src={require('./../../assets/ic-right-slider-blue.svg')} />
                    </div>
                </Swiper>
            </div>
            <div className="materialData">
                {/*<div*/}
                {/*    className="contributionDescription"*/}
                {/*    dangerouslySetInnerHTML={{ __html: contribution.description }}*/}
                {/*/>*/}
                {material && material.description && (
                    <div className="materialInfo">
                        <div className="materialDescription">{material.description}</div>
                        <OriginalInfo data={material} />
                    </div>
                )}
                <div className="labelsContainer">
                    <div className="labels">
                        {material && !isEmpty(material.tags) ? (
                            material.tags.map((tag) =>
                                tag.text.length > 0 ? (
                                    <Link
                                        to={createLocalizedPath(paths.search.toPath({ tag: tag.slug }))}
                                        key={tag.id}
                                        className="label label_clickable"
                                        target="__blank"
                                    >
                                        {tag.text}
                                    </Link>
                                ) : (
                                    <React.Fragment key={tag.id} />
                                )
                            )
                        ) : (
                            <div className="label">{t('No labels')}</div>
                        )}
                    </div>
                    <Button
                        label={isVideo ? t('Label this video') : t('Label this photo')}
                        onClick={() => {
                            if (loggedIn) {
                                setIsLabelMenuOpen(true)
                            } else {
                                dispatch(switchMenu())
                            }
                        }}
                    />
                </div>
            </div>
            {isLabelMenuOpen ? (
                <LabelMenu
                    setIsLabelMenuOpen={setIsLabelMenuOpen}
                    newLabels={newLabels}
                    setNewLabels={setNewLabels}
                    isVideo={isVideo}
                    materialId={material.id}
                    isNecessaryToBuyPro={contribution.pro === 1 && user && !isUserPro(user.member_type)}
                />
            ) : (
                <></>
            )}
        </div>
    )
}

// @ts-ignore
ContributionMaterial.getInitialProps = async ({ store, match }) => {
    // await checkAuth(store)

    const {
        contributions: { contribution }
    } = store.getState()

    const {
        params: { material_id }
    } = match

    if (
        !contribution ||
        !contribution.materials.some((material: ContributionMaterialType) => material.id === Number(material_id))
    ) {
        await store.dispatch(fetchContribution({ material_id }))
        const { contributions } = store.getState()
        if (!contributions.contribution) return { statusCode: 404 }
    }
}

export default ContributionMaterial
